import React, {useState, useEffect} from "react";
import "./RightColumn.css";
import Overlay from "./Overlay";
import Toggle from "./Toggle";

interface Image {
    source: string;
    filename: string;
    x: number;
    y: number;
    imageUrl: string;
}

interface RightColumnProps {
    selectedImage: Image | null;
    images: Image[];
    selectedOptions: string[];
    hideAPNs: boolean;
    filterImage?: string;
}

const RightColumn: React.FC<RightColumnProps> = ({selectedImage, images, selectedOptions, hideAPNs, filterImage}) => {
    const [useHeatmaps, setUseHeatmaps] = useState(false);
    const [closestImages, setClosestImages] = useState<Image[]>([]);
    const [isPanelVisible, setIsPanelVisible] = useState(false);
    const [panelIndex, setPanelIndex] = useState(0);
    const [clickedImage, setClickedImage] = useState<Image | null>(null);

    const handleClosestImageClick = (image: Image) => {
        setClickedImage(image);
    };

    const handleToggle = (isOn: boolean) => {
        setUseHeatmaps(isOn);
    };

    useEffect(() => {
        if (selectedImage) {
            const distances = images.map(image => ({
                ...image,
                distance: Math.sqrt(
                    Math.pow(image.x - selectedImage.x, 2) + Math.pow(image.y - selectedImage.y, 2)
                )
            }));
            distances.sort((a, b) => a.distance - b.distance);
            if (hideAPNs && filterImage) {
                const filteredDistances = distances.filter(image => image.source !== "apn");
                setClosestImages(filteredDistances.slice(1));
            } else {
                setClosestImages(distances.slice(1));
            }
        } else {
            setClosestImages([]);
        }
    }, [selectedImage, images, hideAPNs, filterImage]);

    const handleNext = () => {
        if (panelIndex + 5 < closestImages.length) {
            setPanelIndex(panelIndex + 5);
        }
    };

    const handlePrevious = () => {
        if (panelIndex - 5 >= 0) {
            setPanelIndex(panelIndex - 5);
        }
    };

    const handleImageClick = () => {
        setIsPanelVisible(true);
    };

    const handleClosePanel = (event: React.MouseEvent<HTMLDivElement>) => {
        if (event.target === event.currentTarget) {
            setIsPanelVisible(false);
            setClickedImage(null);
            resetPanel();
        }
    };

    const resetPanel = () => {
        setPanelIndex(0);
    };

    return (
        <div className="right-column">
            <div className="selected-image-container">
                <div className="selected-image-title">Selected Image</div>
                <div className="selected-image-wrapper">
                    <div className="right-column-image-frame">
                        {selectedImage ? (
                            <img
                                src={useHeatmaps ? selectedImage.imageUrl.replace("apn/", "").replace(`${selectedOptions[0].toLowerCase()}/`, '')
                                    .replace('images/', `heatmaps/${selectedOptions[0].toLowerCase()}/${selectedOptions[2]}/${selectedOptions[3].replace(' ', '')}/`) : selectedImage.imageUrl}
                                alt="Selected" className="selected-image" onClick={handleImageClick}/>
                        ) : (
                            <div className="no-image-selected">No image selected</div>
                        )}
                    </div>
                </div>
            </div>
            {/*<div className="textual-description-container">*/}
            {/*    <div className="textual-description-title">Textual Description</div>*/}
            {/*    <div className="textual-description-wrapper">*/}
            {/*        The image shows bla bla bla*/}
            {/*    </div>*/}
            {/*</div>*/}
            <div className="similar-images-container">
                <div className="similar-images-title">Similar Images</div>
                <div className="similar-images-wrapper">
                    {closestImages.slice(0, 2).length > 0 ? (
                        closestImages.slice(0, 2).map((image, index) => (
                            <div key={index} className="placeholder-area">
                                <img
                                    src={useHeatmaps ? image.imageUrl.replace("apn/", "").replace(`${selectedOptions[0].toLowerCase()}/`, '')
                                        .replace('images/', `heatmaps/${selectedOptions[0].toLowerCase()}/${selectedOptions[2]}/${selectedOptions[3].replace(' ', '')}/`) : image.imageUrl}
                                    alt={`Similar ${index + 1}`} className="similar-image"/>
                            </div>
                        ))
                    ) : (
                        <>
                            <div className="no-image-selected-similar">No image selected</div>
                            <div className="no-image-selected-similar">No image selected</div>
                        </>
                    )}
                </div>
            </div>
            <div className="explainability-container">
                <Toggle onToggle={handleToggle} label={"Explainability"} labelPlacement={"end"}/>
            </div>
            {isPanelVisible && selectedImage && (
                <Overlay
                    selectedImage={selectedImage}
                    clickedImage={clickedImage}
                    closestImages={closestImages}
                    panelIndex={panelIndex}
                    useHeatmaps={useHeatmaps}
                    selectedOptions={selectedOptions}
                    handleClosePanel={handleClosePanel}
                    handlePrevious={handlePrevious}
                    handleNext={handleNext}
                    handleClosestImageClick={handleClosestImageClick}
                />
            )}
        </div>
    );
};

export default RightColumn;